/**
 * <product-gallery> — Web Component for the product page gallery.
 *
 * Server renders the hero <img> + thumbnail buttons; this component upgrades
 * them with click and keyboard interaction so a thumbnail click swaps the
 * hero. Without JS, the thumbnails render but do nothing on click — users
 * still see every image in the row.
 *
 * Markup contract:
 *   <product-gallery>
 *     <... data-gallery-hero-wrap>
 *       <img data-gallery-hero src=... srcset=... alt=... />
 *     </...>
 *     <ul>
 *       <li><button data-gallery-thumb data-full-image=... data-full-alt=... data-index=...>...</button></li>
 *       ...
 *     </ul>
 *   </product-gallery>
 *
 * Keyboard:
 *   ArrowRight / ArrowLeft — move focus and activate next/previous thumbnail
 *   Home / End             — jump to first/last thumbnail
 *   Enter / Space          — already activate the focused button (native)
 *
 * Reduced motion: no animated transitions on swap. The hero img element
 * simply has its src/srcset/alt updated.
 */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.hero = this.querySelector('[data-gallery-hero]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

    if (!this.hero || this.thumbs.length === 0) {
      return;
    }

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', this.onThumbClick);
      thumb.addEventListener('keydown', this.onThumbKeydown);
    });
  }

  disconnectedCallback() {
    if (!this.thumbs) return;
    this.thumbs.forEach((thumb) => {
      thumb.removeEventListener('click', this.onThumbClick);
      thumb.removeEventListener('keydown', this.onThumbKeydown);
    });
  }

  onThumbClick = (event) => {
    this.activate(event.currentTarget);
  };

  onThumbKeydown = (event) => {
    const currentIndex = this.thumbs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.thumbs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.thumbs.length) % this.thumbs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.thumbs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextThumb = this.thumbs[nextIndex];
    nextThumb.focus();
    this.activate(nextThumb);
  };

  activate(thumb) {
    const fullImage = thumb.dataset.fullImage;
    const fullAlt = thumb.dataset.fullAlt || '';
    if (!fullImage) return;

    this.hero.src = fullImage;
    this.hero.srcset = '';
    this.hero.alt = fullAlt;

    this.thumbs.forEach((other) => {
      if (other === thumb) {
        other.setAttribute('aria-current', 'true');
        other.classList.add('border-accent');
        other.classList.remove('border-ink-soft');
      } else {
        other.removeAttribute('aria-current');
        other.classList.remove('border-accent');
        other.classList.add('border-ink-soft');
      }
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
